import useFeatureManager from "hooks/featureManager";
import useLanguage from "hooks/language";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ExpressWalletCheckout, {
    EXPRESS_AMOUNT_CENTS,
    EXPRESS_WALLET_CHECKOUT_FLAG,
    WalletCheckoutFlow,
    WalletCheckoutFlowValue,
} from "../../pages/home/screening/payment-field/express-wallet-checkout";
import { FeatureFlag } from "utils/feature-management/amplitude";
import styles from "./booking-flow-payment.module.css";

export function isSecureExpressContext(): boolean {
    if (typeof window === "undefined") {
        return true;
    }
    return window.location.protocol === "https:";
}

type PaymentMethodsLabelProps = {
    sectionTitle?: string;
};

export function PaymentMethodsLabel({
    sectionTitle,
}: PaymentMethodsLabelProps = {}): JSX.Element {
    const { translations } = useLanguage();
    const label =
        sectionTitle ?? translations.bookingFlow.payment.paymentMethod;
    return <div className={styles.methodsLabel}>{label}</div>;
}

function OrPayByCardDivider(): JSX.Element {
    const { translations } = useLanguage();
    return (
        <div className={styles.orPayByCard}>
            <span className={styles.orPayByCardLine} />
            <span className={styles.orPayByCardText}>
                {translations.bookingFlow.payment.orPayByCard}
            </span>
            <span className={styles.orPayByCardLine} />
        </div>
    );
}

type PaymentMethodStackProps = {
    children: React.ReactNode;
};

export function BookingFlowPaymentMethodStack({
    children,
}: PaymentMethodStackProps): JSX.Element {
    return <div className={styles.stack}>{children}</div>;
}

type ExpressWalletSectionProps = {
    flow: WalletCheckoutFlowValue;
    saveCardOnFile?: boolean;
    onPaid: (method: string, paymentMethodId?: string) => Promise<void> | void;
    onProcessingChange?: (isProcessing: boolean) => void;
    onInitialized?: () => void;
    /** Wait for card placeholder fetch before treating wallet iframes as painted. */
    walletPaintSignalEnabled?: boolean;
    paymentMethodsSectionTitle?: string;
    disabled?: boolean;
};

export function BookingFlowExpressWalletSection({
    flow,
    saveCardOnFile = false,
    onPaid,
    onProcessingChange,
    onInitialized,
    walletPaintSignalEnabled = true,
    paymentMethodsSectionTitle,
    disabled = false,
}: ExpressWalletSectionProps): JSX.Element | null {
    const { translations } = useLanguage();
    const tPayment = translations.bookingFlow.payment;
    const [featureManager] = useFeatureManager();
    const [isExpressWalletAvailable, setIsExpressWalletAvailable] = useState(
        false
    );
    const [hasExpressWalletReport, setHasExpressWalletReport] = useState(false);
    const hasSignaledInitializedRef = useRef(false);

    const isExpressEnabled = featureManager?.isFeatureFlagEnabled(
        EXPRESS_WALLET_CHECKOUT_FLAG,
        FeatureFlag.On
    );

    const signalInitialized = useCallback(() => {
        if (hasSignaledInitializedRef.current) {
            return;
        }
        hasSignaledInitializedRef.current = true;
        onInitialized?.();
    }, [onInitialized]);

    useEffect(() => {
        if (!isExpressEnabled) {
            signalInitialized();
        }
    }, [isExpressEnabled, signalInitialized]);

    if (!isSecureExpressContext()) {
        return null;
    }

    if (!featureManager) {
        return null;
    }

    if (!isExpressEnabled) {
        return null;
    }

    if (hasExpressWalletReport && !isExpressWalletAvailable) {
        return (
            <PaymentMethodsLabel sectionTitle={paymentMethodsSectionTitle} />
        );
    }

    const showOrPayByCardDivider =
        hasExpressWalletReport && isExpressWalletAvailable;

    const walletFlow = saveCardOnFile
        ? WalletCheckoutFlow.CardOnFilePublic
        : flow;

    return (
        <>
            <PaymentMethodsLabel sectionTitle={paymentMethodsSectionTitle} />
            <div className={styles.expressHost}>
                <ExpressWalletCheckout
                    flow={walletFlow}
                    hideOrDivider
                    disabled={disabled}
                    onPaid={onPaid}
                    onProcessingChange={onProcessingChange}
                    checkoutMode={saveCardOnFile ? "setup" : "payment"}
                    amountCents={
                        saveCardOnFile ? undefined : EXPRESS_AMOUNT_CENTS
                    }
                    deferredNoShow={saveCardOnFile}
                    saveCard={true}
                    walletPaymentDescription={
                        saveCardOnFile
                            ? tPayment.walletPaymentDescription
                            : undefined
                    }
                    walletNoShowLabel={
                        saveCardOnFile ? tPayment.walletNoShowLabel : undefined
                    }
                    onAvailabilityChange={(isAvailable) => {
                        setHasExpressWalletReport(true);
                        setIsExpressWalletAvailable(isAvailable);
                        if (!isAvailable) {
                            signalInitialized();
                        }
                    }}
                    onWalletButtonsPainted={signalInitialized}
                    walletPaintSignalEnabled={walletPaintSignalEnabled}
                />
            </div>
            {showOrPayByCardDivider && <OrPayByCardDivider />}
        </>
    );
}
